import { create } from 'zustand';
import { MatchingHistoryItem, ActiveIntegrationOption } from '../types';

import { getMatchingHistory } from '../api/getMatchingHistory';

type MatchingHistoryStore = {
  hasError: boolean;
  isLoading: boolean;
  data: MatchingHistoryItem[];
  filters: Record<string, any>;
  sort: any;
  search: Record<string, any>;
  hasMore: boolean;
  nextPageToken: string;
  fetchData: (org_id: string, params?: any) => Promise<void>;
  loadMore: (org_id: string) => Promise<void>;
  setActiveIntegrations: (activeIntegrations: ActiveIntegrationOption[]) => void;
  activeIntegrations: ActiveIntegrationOption[];
  removeRowId: (id: string) => void;
};

const initialState = {
  hasError: false,
  isLoading: false,
  data: [],
  filters: {},
  sort: { field: 'source_product', order: 'ascend' },
  search: {},
  hasMore: true,
  nextPageToken: '',
  activeIntegrations: [],
};

export const useMatchingHistoryStore = create<MatchingHistoryStore>()((set, get) => ({
  ...initialState,
  fetchData: async (org_id: string, params: any) => {
    if (params?.filters) {
      set({ filters: { ...get().filters, ...params?.filters } });
    }
    if (params?.sort) {
      set({ sort: params.sort });
    }
    if (params?.search) {
      set({ search: { ...get().search, ...params?.search } });
    }
    set({ isLoading: true });

    const {
      data: response,
      next_page_token,
      hasError: responseHasError,
    } = await getMatchingHistory({
      org_id,
      filters: get().filters,
      sort: get().sort,
      search: get().search,
    });

    set({
      data: response,
      hasError: responseHasError,
      hasMore: !!next_page_token,
      nextPageToken: next_page_token,
      isLoading: false,
    });
  },
  loadMore: async (org_id: string) => {
    if (!get().hasMore || get().isLoading) return;
    set({ isLoading: true });
    const {
      data: response,
      next_page_token,
      hasError: responseHasError,
    } = await getMatchingHistory({
      org_id,
      filters: get().filters,
      sort: get().sort,
      ...get().search,
      nextPage: get().nextPageToken,
    });

    set({
      data: [...get().data, ...response],
      hasError: responseHasError,
      hasMore: !!next_page_token,
      nextPageToken: next_page_token,
      isLoading: false,
    });
  },
  setActiveIntegrations: (activeIntegrations: ActiveIntegrationOption[]) => {
    set({ activeIntegrations });
  },
  removeRowId: (id: string) => {
    set({
      data: get().data.filter((row) => row.mapping_id !== id),
    });
  },
}));
